import { pb, logWriteError, type Json, type PreferenceRecord } from './pocketbase'

/**
 * The two blobs of per-person settings, and the one place that writes them.
 *
 * `settingsStore` and `rollStore` each own one. They used to carry a debounce
 * and an `upsert` apiece, writing two disjoint columns of the same row, which
 * was duplication PocketBase would have doubled: with no `upsert`, both would
 * have grown the same find-then-create-or-patch dance.
 *
 * It lives in `shared/api` rather than in either entity because an entity may
 * not import another entity, and neither of the two owns this.
 */
export type PreferenceKey = 'roll_settings' | 'app_settings'

/**
 * The record id IS the user id.
 *
 * That is what makes a save one request instead of a lookup followed by a write.
 * The collection also carries a `user` relation, which is not redundant: it is
 * what cascades, so closing an account takes the preferences with it.
 */
const prefs = () => pb.collection('preferences')

async function put(userId: string, body: Record<string, unknown>): Promise<void> {
  try {
    await prefs().update(userId, body)
  } catch (error) {
    if ((error as { status?: number }).status !== 404) throw error
    await prefs().create({ id: userId, user: userId, ...body })
  }
}

/** One timer per key, because the two blobs are written by unrelated actions. */
const timers = new Map<PreferenceKey, ReturnType<typeof setTimeout>>()

/**
 * Write one blob, debounced.
 *
 * `read` is called when the timer fires rather than now, so a burst of changes
 * sends the settled value once instead of the first one repeatedly. Fire and
 * forget: localStorage already has it, and `logWriteError` is what keeps a
 * failure from being invisible.
 */
export function savePreferences(
  userId: string | null | undefined,
  key: PreferenceKey,
  read: () => Json,
  delayMs = 1500,
): void {
  if (!userId) return
  const pending = timers.get(key)
  if (pending) clearTimeout(pending)
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key)
      void put(userId, { [key]: read() }).catch(logWriteError(`save ${key}`))
    }, delayMs),
  )
}

/** Read one blob, or null when this person has never saved any. */
export async function loadPreferences<T>(
  userId: string | null | undefined,
  key: PreferenceKey,
): Promise<T | null> {
  if (!userId) return null
  try {
    const record = await prefs().getOne<PreferenceRecord>(userId)
    return (record[key] as T | null) ?? null
  } catch (error) {
    // No record yet is the ordinary case for somebody who has never changed a
    // setting, so a 404 is silence rather than an error.
    if ((error as { status?: number }).status !== 404) logWriteError(`load ${key}`)(error)
    return null
  }
}
